import { useState, useEffect } from 'react';
import signalRService from '../lib/api/signalRService';

/**
 * Hook to manage the Pokket thinking indicator and confidence score
 * 
 * @param {boolean} connectionEstablished - Whether the SignalR connection is established
 * @param {Array} messages - The current chat messages
 * @param {string} sessionId - The current chat session ID
 * @returns {Object} Thinking indicator state and controls
 */
const useThinkingIndicator = (connectionEstablished, messages, sessionId) => {
  const [botIsThinking, setBotIsThinking] = useState(false);
  const [confidenceScore, setConfidenceScore] = useState(() => {
    // Initialize from localStorage if available
    const cachedScore = localStorage.getItem('pokket_confidence_score');
    return cachedScore ? parseInt(cachedScore, 10) : 0;
  });
  const DEBUG = true; // Match the DEBUG flag from ChatPage
  
  // Listen for SolutionConfidence from SignalR
  useEffect(() => {
    if (!connectionEstablished) return;
    
    // Use signalRService for event registration
    const handleConfidence = (data) => {
      // Always log the confidence value for debugging, regardless of DEBUG flag
      console.log(`%c📊 SolutionConfidence: ${data?.confidence}`, 'color: purple; font-weight: bold');
      
      if (DEBUG) console.log("Received solution confidence:", data);
      if (data && typeof data.confidence === 'number') {
        // Update state
        setConfidenceScore(data.confidence);
        
        // Cache the confidence score
        localStorage.setItem('pokket_confidence_score', data.confidence.toString());
      }
    };
    
    // Register a handler for the SolutionConfidence event
    signalRService.connection.on('SolutionConfidence', handleConfidence);
    
    // Clean up function to remove the event handler
    return () => {
      if (signalRService.connection) {
        signalRService.connection.off('SolutionConfidence', handleConfidence);
      }
    };
  }, [connectionEstablished, DEBUG]);
  
  // Reset confidence score when bot starts thinking (new user message)
  useEffect(() => {
    if (botIsThinking) {
      // Reset confidence to 0 when bot starts thinking about a new message
      setConfidenceScore(0);
      // Clear the cached confidence score to prevent it from persisting
      localStorage.removeItem('pokket_confidence_score');
      
      if (DEBUG) console.log('Bot is thinking about a new message, reset confidence score to 0');
    }
  }, [botIsThinking, DEBUG]);
  
  // Add a separate effect to listen for message responses and stop thinking indicator
  useEffect(() => {
    if (!connectionEstablished) return;
    
    // Add event listener for chat messages to turn off thinking indicator
    const unsubscribeMessages = signalRService.onMessage((message) => {
      // If we receive a message from the bot, stop the thinking indicator
      if (message && !message.isFromUser) {
        if (DEBUG) console.log('Bot response received, stopping thinking indicator');
        setBotIsThinking(false);
      }
    });
    
    // Clean up
    return () => {
      unsubscribeMessages();
    };
  }, [connectionEstablished, DEBUG]);
  
  // Effect to detect when a bot message is added locally
  useEffect(() => {
    // If messages array has a bot message at the end and bot is thinking, 
    // stop the thinking indicator (handles fallback and local additions)
    if (
      messages.length > 0 && 
      messages[messages.length - 1].sender === 'bot' && 
      botIsThinking
    ) {
      if (DEBUG) console.log('Bot message detected in messages array, stopping thinking indicator');
      setBotIsThinking(false);
    }
  }, [messages, botIsThinking, DEBUG]);
  
  return {
    botIsThinking,
    setBotIsThinking,
    confidenceScore
  };
};

export default useThinkingIndicator; 